/**
 * A drag-to-commit control for irreversible actions — the handle must be carried the length of
 * the track before anything happens.
 *
 * **Why not a button.** A tap is one gesture and a mis-tap is indistinguishable from an intent;
 * for an action that cannot be undone, the value of this control is precisely that it cannot be
 * triggered by a stray touch, a double-tap landing on a re-rendered layout, or a screen-reader
 * "activate" on the wrong row. Pair it with a typed confirmation, never use it alone.
 *
 * **Direction follows the writing direction, read from the DOM rather than assumed.** The shell
 * is RTL, so the handle rests at the *right* and travels left; the same component in an LTR
 * context travels the other way. `getComputedStyle().direction` is the only honest source —
 * a hardcoded sign is the same class of bug as a hardcoded `left`, and CSS logical properties
 * position the handle but cannot tell this file which way a pointer moved.
 *
 * **The keyboard path is not a shortcut around the gesture.** `role="slider"` with arrow-key
 * stepping is what makes this reachable at all without a pointer; it commits on reaching the
 * end of the track, the same place the drag commits, rather than on `Enter` at rest.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

/** How far along the track counts as "carried the whole way". Not 1: a pointer released a pixel
 *  short of the end is an intent to commit, and a track that refuses it reads as broken. */
const COMMIT_AT = 0.96

/** One arrow press. Four presses cross the track — deliberate, and not a single keystroke. */
const KEY_STEP = 0.25

/**
 * Which way the handle travels.
 *
 * **The nearest `dir` attribute is consulted before the computed style**, and that order is not
 * cosmetic: the shell sets `<html dir="rtl">`, so the attribute is the authoritative signal here,
 * and it is also the only one jsdom reports — `getComputedStyle().direction` returns `ltr` there
 * regardless of `dir`, which would make every test of this logic silently exercise the LTR branch
 * and pass. The computed style stays as the fallback for a container styled by CSS alone.
 */
function isRtl(element: Element): boolean {
  const attribute = element.closest('[dir]')?.getAttribute('dir')?.toLowerCase()
  if (attribute === 'rtl' || attribute === 'ltr') return attribute === 'rtl'
  return getComputedStyle(element).direction === 'rtl'
}

export function SlideToConfirm({
  label,
  committedLabel,
  disabled = false,
  busy,
  onConfirm,
}: {
  /** What the control will do, shown on the track while the handle is at rest. */
  readonly label: string
  /** Shown once the gesture completes and the action is in flight. */
  readonly committedLabel: string
  readonly disabled?: boolean
  /**
   * The caller's action is running. **Deliberately not defaulted to `false`** — `undefined` and
   * `false` mean different things to the release below: a caller that never reports `busy` is one
   * whose action cannot finish while this control is still mounted, and it keeps the original
   * one-commit-per-mount contract.
   */
  readonly busy?: boolean
  onConfirm(): void
}): JSX.Element {
  const trackRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState(0)
  const [dragging, setDragging] = useState(false)
  // Guards the commit itself, not the gesture: a pointer that crosses the threshold, is released,
  // and is re-dragged before the parent unmounts must not fire the action twice.
  const committedRef = useRef(false)

  const locked = disabled || busy === true

  useEffect(() => {
    // A control that becomes unavailable mid-drag returns to rest rather than keeping a handle
    // stranded partway along a track it can no longer commit.
    if (locked && !committedRef.current) setProgress(0)
  }, [locked])

  /**
   * **The commit guard releases when the caller's action finishes without unmounting — and it
   * releases HERE rather than at either call site.**
   *
   * `committedRef` is what makes one gesture one dispatch. Left set, it also makes a *failed*
   * attempt permanent: the track stays full, reading `committedLabel`, with the caller's error
   * line under it and a `commit()` that early-returns forever. The only recovery was closing the
   * dialog and reopening it, which nothing signposts — so the control read as broken on the one
   * kind of action this component exists for.
   *
   * **The condition is `busy === false` at a render where the guard is set — NOT `busy` falling
   * from `true`, which was written first and is wrong.** The commit and the caller's
   * `setBusy(true)` are one batch, so an action that settles before React flushes that batch —
   * a synchronous throw, a rejected promise resolved on the first microtask, anything a
   * `SessionUnavailableError` produces — coalesces `true`→`false` and **commits no busy render at
   * all**. A watcher for the falling edge sees nothing and leaves the slider stuck on exactly the
   * failures that arrive fastest. (Found by a case driving a rejected request, not by inspection;
   * `App.test.tsx` → *names a network failure and leaves the dialog usable*.)
   *
   * **`undefined` is the third state and it is what preserves the old contract.** A caller that
   * never reports `busy` cannot finish while this control is mounted, so it keeps one commit per
   * mount; that is why {@link busy} is not defaulted.
   *
   * `progress` is in the deps because the coalesced case changes nothing else: `busy` reads
   * `false` before the gesture and after it, and the commit is the only thing that moved.
   *
   * **Both irreversible dialogs share this component** — `DeleteAccountDialog` and
   * `DeleteGateDialog` — so the fix belongs here. A `key` remount at one call site would have left
   * the identical stuck slider on the other, which is the actual shape the defect shipped in.
   */
  useEffect(() => {
    if (busy !== false) return
    if (!committedRef.current) return
    committedRef.current = false
    setProgress(0)
  }, [busy, progress])

  const progressFor = useCallback((clientX: number): number => {
    const track = trackRef.current
    const handle = handleRef.current
    if (track === null || handle === null) return 0
    const rect = track.getBoundingClientRect()
    const width = handle.getBoundingClientRect().width
    const span = rect.width - width
    if (span <= 0) return 0
    // The pointer is treated as holding the *centre* of the handle, which is where a thumb
    // actually lands; measuring from the handle's leading edge makes the last few pixels
    // unreachable and the control feel like it refuses a completed gesture.
    const travelled = isRtl(track) ? rect.right - width / 2 - clientX : clientX - rect.left - width / 2
    return Math.min(1, Math.max(0, travelled / span))
  }, [])

  const commit = useCallback((): void => {
    if (committedRef.current) return
    committedRef.current = true
    setProgress(1)
    onConfirm()
  }, [onConfirm])

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>): void {
    if (locked) return
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragging(true)
    setProgress(progressFor(event.clientX))
  }

  function onPointerMove(event: React.PointerEvent<HTMLDivElement>): void {
    if (!dragging || locked) return
    setProgress(progressFor(event.clientX))
  }

  function endDrag(event: React.PointerEvent<HTMLDivElement>): void {
    if (!dragging) return
    setDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (progress >= COMMIT_AT) commit()
    else setProgress(0)
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (locked) return
    // Forward/back are named in *travel* terms, not screen terms: in RTL the handle advances
    // leftwards, so ArrowLeft is forward there and ArrowRight is back.
    const rtl = trackRef.current !== null && isRtl(trackRef.current)
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight'
    const back = rtl ? 'ArrowRight' : 'ArrowLeft'
    let next: number | undefined
    if (event.key === forward) next = Math.min(1, progress + KEY_STEP)
    else if (event.key === back) next = Math.max(0, progress - KEY_STEP)
    else if (event.key === 'End') next = 1
    else if (event.key === 'Home') next = 0
    if (next === undefined) return
    event.preventDefault()
    setProgress(next)
    if (next >= COMMIT_AT) commit()
  }

  const percent = Math.round(progress * 100)
  return (
    <div
      ref={trackRef}
      className={`slide-confirm${locked ? ' locked' : ''}${dragging ? ' dragging' : ''}${progress >= COMMIT_AT ? ' committed' : ''}`}
    >
      {/* `--slide-handle` is the handle's own size, declared once in `global.css`; both the fill
          and the handle offset are expressed against it so the handle's trailing edge lands
          exactly on the end of the track at 100% instead of overshooting by its own width. */}
      <span
        className="slide-confirm-fill"
        style={{ inlineSize: `calc(var(--slide-handle) + (100% - var(--slide-handle)) * ${progress})` }}
        aria-hidden="true"
      />
      <span className="slide-confirm-label">{progress >= COMMIT_AT ? committedLabel : label}</span>
      <div
        ref={handleRef}
        className="slide-confirm-handle"
        role="slider"
        tabIndex={locked ? -1 : 0}
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={`${percent}%`}
        aria-disabled={locked}
        // The handle is the only thing that moves, so it owns the offset. `inset-inline-start`
        // rather than `left`: the track is laid out logically and the RTL case must not need a
        // second rule to undo an LTR one.
        style={{ insetInlineStart: `calc((100% - var(--slide-handle)) * ${progress})` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
      >
        <span className="slide-confirm-chevrons" aria-hidden="true" />
      </div>
    </div>
  )
}
